<script setup lang="ts">
import { computed } from 'vue'
import { useToasts } from '@/lib/toasts'

const { toasts, dismiss, pause, resume } = useToasts()
// The polite region stays mounted even when empty: a status region added along
// with its first message is often not announced. An alert is announced as it is
// inserted, so its region mounts only while it holds one; an empty alert on
// every page would also muddy the page's own alerts.
const regions = computed(() =>
  [
    {
      tone: 'error',
      role: 'alert',
      live: 'assertive',
      items: toasts.value.filter((toast) => toast.tone === 'error'),
    },
    {
      tone: 'success',
      role: 'status',
      live: 'polite',
      items: toasts.value.filter((toast) => toast.tone === 'success'),
    },
  ].filter((region) => region.tone === 'success' || region.items.length > 0),
)
</script>

<template>
  <div class="toaster">
    <TransitionGroup
      v-for="region in regions"
      :key="region.tone"
      tag="div"
      name="toast"
      class="toast-region"
      :role="region.role"
      :aria-live="region.live"
    >
      <div
        v-for="toast in region.items"
        :key="toast.id"
        class="toast"
        :class="`toast-${toast.tone}`"
        @pointerenter="pause(toast.id)"
        @pointerleave="resume(toast.id)"
        @focusin="pause(toast.id)"
        @focusout="resume(toast.id)"
      >
        <svg class="toast-icon" viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
          <path
            v-if="toast.tone === 'success'"
            d="M4.5 10.5l3.5 3.5 7.5-8"
            fill="none"
            stroke="currentColor"
            stroke-width="2.25"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            v-else
            d="M10 4.5v7M10 15.2v.3"
            fill="none"
            stroke="currentColor"
            stroke-width="2.25"
            stroke-linecap="round"
          />
        </svg>
        <p>{{ toast.message }}</p>
        <button
          type="button"
          class="toast-close"
          aria-label="Dismiss notification"
          @click="dismiss(toast.id)"
        >
          <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
            <path
              d="M5 5l10 10M15 5L5 15"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
/* Fixed at the top so a confirmation is seen wherever the page is scrolled.
   The stack itself lets clicks through; only the toasts take them. */
.toaster {
  position: fixed;
  top: calc(env(safe-area-inset-top, 0px) + 0.75rem);
  left: 50%;
  z-index: 1000;
  width: min(26rem, calc(100vw - 2rem));
  transform: translateX(-50%);
  pointer-events: none;
  font-family: ui-sans-serif, system-ui, sans-serif;
}
.toast-region {
  position: relative;
}
.toast {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  margin-bottom: 0.5rem;
  padding: 0.7rem 0.6rem 0.7rem 0.9rem;
  border-radius: 6px;
  background: #17221d;
  color: #fbfaf4;
  box-shadow: 0 12px 28px -12px rgba(23, 34, 29, 0.55);
  pointer-events: auto;
}
.toast-error {
  background: #4a1d14;
}
.toast-icon {
  flex: none;
  margin-top: 0.1rem;
  color: #b9c967;
}
.toast-error .toast-icon {
  color: #f0a898;
}
.toast p {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 0.92rem;
  font-weight: 600;
  line-height: 1.4;
  overflow-wrap: anywhere;
}
.toast-close {
  flex: none;
  display: inline-grid;
  place-items: center;
  width: 1.75rem;
  height: 1.75rem;
  margin: -0.2rem 0;
  padding: 0;
  border: 0;
  border-radius: 3px;
  background: none;
  color: inherit;
  opacity: 0.8;
  cursor: pointer;
}
.toast-close:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.12);
}
.toast-close:focus-visible {
  outline: 3px solid #b9c967;
  outline-offset: 1px;
  opacity: 1;
}
.toast-enter-active,
.toast-leave-active,
.toast-move {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(-0.5rem);
}
/* Leaving toasts drop out of the flow so the rest slide up smoothly. */
.toast-leave-active {
  position: absolute;
  left: 0;
  right: 0;
}
@media (prefers-reduced-motion: reduce) {
  .toast-enter-active,
  .toast-leave-active,
  .toast-move {
    transition: none;
  }
}
</style>
